import React, { useState } from "react";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useLanguage } from "../../Translations/LenguageContext";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import ErrorMessage from "../../Components/ErrorMessage";
import Spinner from "../../Components/Spinner";
import SupportedStripeCountries from "../../JSON/SupportedStripeCountries.json";

const AddCardModal = ({ show, handleClose, fetchBilling }) => {
    const { t } = useLanguage();
    const user_id = sessionStorage.getItem("user_id");
    const stripe = useStripe();
    const elements = useElements();
    const [pricingInfo, setPricingInfo] = useState(false);
    const [err, setErr] = useState({ message: "", display: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false)
    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [country, setCountry] = useState("");
    const [city, setCity] = useState("");
    const [street, setStreet] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [additionalInfo, setAdditionalInfo] = useState("");
    const [cardComplete, setCardComplete] = useState(false);


    const togglePricingInfo = () => setPricingInfo(!pricingInfo);

    const validateInputs = () => {
        setErr({ message: "", display: false })
        setLoading(true)
        const newErrors = {};

        if (!name.trim()) newErrors.name = true;
        if (!surname.trim()) newErrors.surname = true;
        if (!country.trim()) newErrors.country = true;
        if (!city.trim()) newErrors.city = true;
        if (!street.trim()) newErrors.street = true;
        if (!postalCode.trim()) newErrors.postalCode = true;

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setLoading(false)
            setErr({ message: "Please fill in all required fields.", display: true });
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        setErr({ message: "", display: false })
        setLoading(true)
        e.preventDefault();

        if (!validateInputs()) return;

        if (!stripe || !elements) return;

        const cardElement = elements.getElement(CardElement);
        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
        });

        if (error) {
            return;
        }

        const cardDetails = paymentMethod.card;

        const payload = {
            user_id,
            payment_method_id: paymentMethod.id,
            name,
            surname,
            country,
            city,
            street,
            postal_code: postalCode,
            additional_info: additionalInfo,
            card_details: {
                last4: cardDetails.last4,
                exp_month: cardDetails.exp_month,
                brand: cardDetails.brand,
                exp_year: cardDetails.exp_year,
            }
        };

        try {
            const response = await fetch("https://backend.erioon.com/save_billing_card", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            setLoading(false)
            const data = await response.json();
            if (!response.ok) {
                setErr({ message: `${data.error}`, display: true });
            } else {
                closeModal()
                fetchBilling()
            }
        } catch (err) {
            setLoading(false)
            console.error(err);
            setErr({ message: `${err.message || err.error}`, display: true });
        }
    };

    const isFormComplete = () =>
        cardComplete &&
        stripe;


    const closeModal = () => {
        handleClose()
        setName("")
        setSurname("")
        setCountry("")
        setCity("")
        setStreet("")
        setPostalCode("")
        setAdditionalInfo("")
        setErr({ message: "", display: false })
        setLoading(false)
    }


    return (
        <Modal show={show} onHide={closeModal} className="modal_pricing">
            <div className="share_main_cont_modal">

                <p className="share_label">
                    Add payment method{" "}
                    <OverlayTrigger
                        placement="right"
                        overlay={
                            <Tooltip className="stripe_tooltip">
                                <p className="tooltip_title">Where are card details stored?</p>
                                <p className="tooltip_desc">
                                    We never store your card information directly. All payments are securely processed and stored by Stripe, a trusted payment provider.
                                </p>
                            </Tooltip>
                        }
                    >
                        <i className="fa-solid fa-info"></i>
                    </OverlayTrigger>
                </p>
                <p className="share_label_desc">
                    Securely add a payment method to fund your credits and access Erioon resources.
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="modal_body_pricing">
                        <p className="modal_pre_body_pricing_title">Card details</p>
                        <div className="add_card_info_cont">
                            <CardElement
                                options={{ hidePostalCode: true }}
                                onChange={(event) => setCardComplete(event.complete)}
                            />
                        </div>

                        <p className="modal_pre_body_pricing_title">Card holder info</p>
                        <div className="card_input_cont">
                            <input className={`card_input ${errors.name ? "error_border" : ""}`} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
                            <input className={`card_input ${errors.surname ? "error_border" : ""}`} placeholder="Surname" value={surname} onChange={(e) => setSurname(e.target.value)} />
                        </div>

                        <p className="modal_pre_body_pricing_title">Billing address</p>
                        <div className="card_input_cont">
                            <select
                                className={`card_input ${errors.country ? "error_border" : ""}`}
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                            >
                                <option value="">Select a country</option>
                                {SupportedStripeCountries.map((country) => (
                                    <option key={country.value} value={country.value}>
                                        {country.label}
                                    </option>
                                ))}
                            </select>
                            <input className={`card_input ${errors.city ? "error_border" : ""}`} placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
                        </div>

                        <div className="card_input_cont">
                            <input className={`card_input ${errors.street ? "error_border" : ""}`} placeholder="Street & N°" value={street} onChange={(e) => setStreet(e.target.value)} />
                            <input className={`card_input ${errors.postalCode ? "error_border" : ""}`} placeholder="Postal code" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
                        </div>

                        <div className="card_input_cont">
                            <textarea className="card_input" placeholder="Additional information" value={additionalInfo} onChange={(e) => setAdditionalInfo(e.target.value)} />
                        </div>
                    </div>

                    <div className="modal_footer">
                        <ErrorMessage err={err} />

                        <div className="footer_btn_cont">
                            <button disabled={!isFormComplete() || loading} className="save_btn">
                                {loading ? (
                                    <Spinner loading={loading} />
                                ) : (
                                    <>
                                     Save payment method
                                    </>
                                )}
                            </button>

                            <button type="button" className="cancel_btn" onClick={closeModal}>{t.cancel}</button>
                        </div>
                    </div>
                </form>
            </div>

        </Modal>
    );
};

export default AddCardModal;
